class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);

const exactSearchProductTypes = {
  'floor lamp': ['Lighting > Floor Lamps'],
  'floor lamps': ['Lighting > Floor Lamps'],
  'table lamp': ['Lighting > Table Lamps', 'Lighting > Cordless Lamps'],
  'table lamps': ['Lighting > Table Lamps', 'Lighting > Cordless Lamps'],
  'desk lamp': ['Lighting > Table Lamps'],
  'desk lamps': ['Lighting > Table Lamps'],
  'mirror': ['Decor > Mirrors'],
  'mirrors': ['Decor > Mirrors'],
  'wall mirror': ['Decor > Mirrors'],
  'wall mirrors': ['Decor > Mirrors'],
  'floor mirror': ['Decor > Mirrors'],
  'floor mirrors': ['Decor > Mirrors'],
  'vanity mirror': ['Decor > Mirrors'],
  'vanity mirrors': ['Decor > Mirrors'],
  'ceiling light': ['Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Chandeliers', 'Lighting > Wall Light', 'Lighting > Outdoor Lighting', 'Lighting > Recessed Lighting', 'Lighting > Surface Mount Lighting'],
  'ceiling lights': ['Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Chandeliers', 'Lighting > Wall Light', 'Lighting > Outdoor Lighting', 'Lighting > Recessed Lighting', 'Lighting > Surface Mount Lighting'],
  'flush mount': ['Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Chandeliers', 'Lighting > Wall Light', 'Lighting > Outdoor Lighting', 'Lighting > Recessed Lighting', 'Lighting > Surface Mount Lighting'],
  'flush mounts': ['Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Chandeliers', 'Lighting > Wall Light', 'Lighting > Outdoor Lighting', 'Lighting > Recessed Lighting', 'Lighting > Surface Mount Lighting'],
  'semi flush mount': ['Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Chandeliers', 'Lighting > Wall Light', 'Lighting > Outdoor Lighting', 'Lighting > Recessed Lighting', 'Lighting > Surface Mount Lighting'],
  'semi flush mounts': ['Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Chandeliers', 'Lighting > Wall Light', 'Lighting > Outdoor Lighting', 'Lighting > Recessed Lighting', 'Lighting > Surface Mount Lighting'],
  'semi-flush mount': ['Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Chandeliers', 'Lighting > Wall Light', 'Lighting > Outdoor Lighting', 'Lighting > Recessed Lighting', 'Lighting > Surface Mount Lighting'],
  'semi-flush mounts': ['Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Chandeliers', 'Lighting > Wall Light', 'Lighting > Outdoor Lighting', 'Lighting > Recessed Lighting', 'Lighting > Surface Mount Lighting'],
  'accent table': ['Furniture > Accent Tables'],
  'accent tables': ['Furniture > Accent Tables'],
  'side table': ['Furniture > Accent Tables'],
  'side tables': ['Furniture > Accent Tables'],
  'end table': ['Furniture > Accent Tables'],
  'end tables': ['Furniture > Accent Tables'],
  'console table': ['Furniture > Console Tables'],
  'console tables': ['Furniture > Console Tables'],
  'dining table': ['Furniture > Dining Tables'],
  'dining tables': ['Furniture > Dining Tables'],
  'desk': ['Furniture > Desks'],
  'desks': ['Furniture > Desks'],
  'writing desk': ['Furniture > Desks'],
  'writing desks': ['Furniture > Desks'],
  'dresser': ['Furniture > Dressers'],
  'dressers': ['Furniture > Dressers'],
  'chest of drawers': ['Furniture > Dressers'],
  'tv stand': ['Furniture > Entertainment Centers & TV Stands'],
  'tv stands': ['Furniture > Entertainment Centers & TV Stands'],
  'entertainment center': ['Furniture > Entertainment Centers & TV Stands'],
  'entertainment centers': ['Furniture > Entertainment Centers & TV Stands'],
  'bookcase': ['Furniture > Shelving'],
  'bookcases': ['Furniture > Shelving'],
  'bookshelf': ['Furniture > Shelving'],
  'bookshelves': ['Furniture > Shelving'],
  'shelf': ['Furniture > Shelving'],
  'shelves': ['Furniture > Shelving'],
  'bar cart': ['Furniture > Bar Carts'],
  'bar carts': ['Furniture > Bar Carts'],
  'chandelier': ['Lighting > Chandeliers'],
  'chandeliers': ['Lighting > Chandeliers'],
  'pendant': ['Lighting > Pendant Light', 'Lighting > Linear Suspension', 'Lighting > Chandeliers'],
  'pendants': ['Lighting > Pendant Light', 'Lighting > Linear Suspension', 'Lighting > Chandeliers'],
  'pendant light': ['Lighting > Pendant Light', 'Lighting > Linear Suspension', 'Lighting > Chandeliers'],
  'pendant lights': ['Lighting > Pendant Light', 'Lighting > Linear Suspension', 'Lighting > Chandeliers'],
  'wall light': ['Lighting > Wall Light', 'Lighting > Outdoor Lighting'],
  'wall lights': ['Lighting > Wall Light', 'Lighting > Outdoor Lighting'],
  'wall lamp': ['Lighting > Wall Light', 'Lighting > Cordless Lamps'],
  'wall lamps': ['Lighting > Wall Light', 'Lighting > Cordless Lamps'],
  'sconce': ['Lighting > Wall Light', 'Lighting > Bath Lighting', 'Lighting > Cordless Lamps', 'Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Outdoor Lighting', 'Lighting > Chandeliers', 'Lighting > Hardware and Accessories'],
  'sconces': ['Lighting > Wall Light', 'Lighting > Bath Lighting', 'Lighting > Cordless Lamps', 'Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Outdoor Lighting', 'Lighting > Chandeliers', 'Lighting > Hardware and Accessories'],
  'bath light': ['Lighting > Bath Lighting'],
  'bath lights': ['Lighting > Bath Lighting'],
  'vanity light': ['Lighting > Bath Lighting'],
  'vanity lights': ['Lighting > Bath Lighting'],
  'bathroom vanity light': ['Lighting > Bath Lighting'],
  'bathroom vanity lights': ['Lighting > Bath Lighting'],
  'ceiling fan': ['Lighting > Ceiling Fan'],
  'ceiling fans': ['Lighting > Ceiling Fan'],
  'picture light': ['Lighting > Picture Lights'],
  'picture lights': ['Lighting > Picture Lights'],
  'outdoor light': ['Lighting > Outdoor Lighting'],
  'outdoor lights': ['Lighting > Outdoor Lighting'],
  'outdoor lighting': ['Lighting > Outdoor Lighting'],
  'recessed light': ['Lighting > Recessed Lighting'],
  'recessed lights': ['Lighting > Recessed Lighting'],
  'recessed lighting': ['Lighting > Recessed Lighting'],
  'track light': ['Lighting > Track Lighting'],
  'track lights': ['Lighting > Track Lighting'],
  'track lighting': ['Lighting > Track Lighting'],
  'candle holder': ['Decor > Candle Holders'],
  'candle holders': ['Decor > Candle Holders'],
  'clock': ['Decor > Clocks'],
  'clocks': ['Decor > Clocks'],
  'home fragrance': ['Decor > Home Fragrances'],
  'home fragrances': ['Decor > Home Fragrances'],
  'diffuser': ['Decor > Home Fragrances'],
  'diffusers': ['Decor > Home Fragrances'],
  'rug': ['Decor > Rugs'],
  'rugs': ['Decor > Rugs'],
  'vase': ['Decor > Vases'],
  'vases': ['Decor > Vases'],
  'wall art': ['Decor > Wall Art'],
  'wall artwork': ['Decor > Wall Art'],
  'bath accessories': ['Decor > Bath'],
  'bath accessory': ['Decor > Bath'],
  'bath decor': ['Decor > Bath'],
  'throw blanket': ['Decor > Throws & Blankets'],
  'throw blankets': ['Decor > Throws & Blankets'],
  'throw': ['Decor > Throws & Blankets'],
  'throws': ['Decor > Throws & Blankets'],
  'tableware': ['Kitchen & Dining'],
  'dinnerware': ['Kitchen & Dining'],
  'serveware': ['Kitchen & Dining'],
  'kitchen and dining': ['Kitchen & Dining'],
  'coffee table': ['Furniture > Coffee Tables'],
  'coffee tables': ['Furniture > Coffee Tables'],
  'wall sconce': ['Lighting > Wall Light', 'Lighting > Bath Lighting', 'Lighting > Cordless Lamps', 'Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Outdoor Lighting', 'Lighting > Chandeliers'],
  'wall sconces': ['Lighting > Wall Light', 'Lighting > Bath Lighting', 'Lighting > Cordless Lamps', 'Lighting > Ceiling Light', 'Lighting > Pendant Light', 'Lighting > Outdoor Lighting', 'Lighting > Chandeliers'],
  'dining chair': ['Furniture > Dining Chairs'],
  'dining chairs': ['Furniture > Dining Chairs']
};

function enforceExactSearchProductType() {
  if (window.location.pathname !== '/search') return;

  const params = new URLSearchParams(window.location.search);
  const terms = (params.get('q') || '')
    .trim()
    .replace(/^"(.*)"$/, '$1')
    .toLowerCase()
    .replace(/\s+/g, ' ');
  const productTypes = exactSearchProductTypes[terms];
  if (!productTypes) return;

  if (productTypes.length === 1) {
    if (params.get('filter.p.product_type') === productTypes[0]) return;
    params.set('filter.p.product_type', productTypes[0]);
  } else {
    // Shopify's storefront URL filter accepts one product type at a time.
    // Keep the query unfiltered here; Liquid narrows recognized searches to this type list.
    if (!params.has('filter.p.product_type')) return;
    params.delete('filter.p.product_type');
  }

  window.location.replace(`${window.location.pathname}?${params.toString()}`);
}

enforceExactSearchProductType();


// Keep recognized product searches inside their matching product types.
document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[role="search"]');
  if (!form) return;

  const queryInput = form.querySelector('[name="q"]');
  if (!queryInput) return;

  const terms = queryInput.value.trim().replace(/^"(.*)"$/, '$1').toLowerCase().replace(/\s+/g, ' ');
  form.querySelectorAll('input[name="filter.p.product_type"]').forEach((input) => input.remove());

  const productTypes = exactSearchProductTypes[terms];
  if (productTypes && productTypes.length === 1) {
    const typeFilter = document.createElement('input');
    typeFilter.type = 'hidden';
    typeFilter.name = 'filter.p.product_type';
    typeFilter.value = productTypes[0];
    form.appendChild(typeFilter);
  }
}, true);
